import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { queryClient } from './queryClient';
import { isFeatureEnabled } from '../utils/featureFlags';
import { pageIdForPath, pathForPageId } from '../utils/routes';
import { GuestBookPageMount } from '../screens/GuestBookPage';
import { SlotManagementPageMount, SlotSummaryPageMount } from '../screens/SlotSummaryPage';
import { AccountsPageMount, RefundInfoMount } from '../screens/AccountsPage';
import { ReportsPageMount } from '../screens/ReportsPage';
import { TeamMonitorPageMount } from '../screens/TeamMonitorPage';
import '../styles/nexus-react.css';

function ReactMountStatus() {
  const enabledScreens = [
    ['Today\'s Reservations', 'USE_REACT_TODAYS_RESERVATIONS'],
    ['All Reservations', 'USE_REACT_ALL_RESERVATIONS'],
    ['Caller Console', 'USE_REACT_CALLER_CONSOLE'],
    ['Slot Summary', 'USE_REACT_SLOT_SUMMARY'],
    ['Slot Analytics', 'USE_REACT_SLOT_ANALYTICS'],
    ['Dashboard', 'USE_REACT_DASHBOARD'],
    ['Guest Book', 'USE_REACT_GUEST_BOOK']
  ].filter(([, flag]) => isFeatureEnabled(flag));

  if (!enabledScreens.length) return null;

  return (
    <div className="nexus-react-status" hidden>
      {enabledScreens.map(([name]) => name).join(', ')}
    </div>
  );
}

function findNavButton(pageId) {
  const explicit = document.querySelector(`[data-page="${pageId}"]`);
  if (explicit) return explicit;
  const navMap = window.NAV_PAGE_BY_ID || {};
  const match = Object.entries(navMap).find(([, mappedPage]) => mappedPage === pageId);
  return match ? document.getElementById(match[0]) : null;
}

function LegacyRouteBridge() {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    window.NexusReactNavigate = (pageId, options = {}) => {
      const path = pathForPageId(pageId);
      if (path === window.location.pathname) return;
      navigate(path, { replace: Boolean(options.replace) });
    };
    return () => {
      if (window.NexusReactNavigate) delete window.NexusReactNavigate;
    };
  }, [navigate]);

  React.useEffect(() => {
    const pageId = pageIdForPath(location.pathname);
    if (!pageId) return;
    window.requestAnimationFrame(() => {
      if (typeof window.showPage !== 'function') return;
      window.showPage(pageId, findNavButton(pageId), { skipRoute: true });
    });
  }, [location.pathname]);

  return null;
}

function RouteScopedMounts() {
  const location = useLocation();
  const pageId = pageIdForPath(location.pathname);

  return (
    <>
      {pageId === 'customerbook' ? <GuestBookPageMount /> : null}
      {pageId === 'slotsummary' ? <SlotSummaryPageMount /> : null}
      {pageId === 'slotmanagement' ? <SlotManagementPageMount /> : null}
      {pageId === 'advances' ? <AccountsPageMount type="advances" /> : null}
      {pageId === 'refunds' ? <AccountsPageMount type="refunds" /> : null}
      {pageId === 'reportadvance' ? <ReportsPageMount type="advance" /> : null}
      {pageId === 'reportcovers' ? <ReportsPageMount type="covers" /> : null}
      {pageId === 'reportreconciliation' ? <ReportsPageMount type="reconciliation" /> : null}
      {pageId === 'reportinsight' ? <ReportsPageMount type="insight" /> : null}
      {pageId === 'reportfeedback' ? <ReportsPageMount type="feedback" /> : null}
      {pageId === 'teammonitor' ? <TeamMonitorPageMount /> : null}
    </>
  );
}

export function NexusReactRoot() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ReactMountStatus />
        <LegacyRouteBridge />
        <RefundInfoMount />
        <RouteScopedMounts />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
